import { Spin } from 'antd';
import type { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  TEMPLATE_SOURCE_NONE,
  parseTemplateDutyPointKey,
  templateDutyPointCodeOf,
  templateDutyPointLabelOf,
  templateDutyPointPhraseOf,
  templateDutyPointRowsOf,
  type ArrangementTemplateDto,
  type TemplateCheckDto,
  type TemplateDutyPointDto,
  type TemplateGroupDto,
  type TemplateSetDto,
} from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { StatusPill } from '@/components/StatusPill';
import { orderStatusWord, templateStateWord } from '@/lib/statusPresentation';
import { templateDecisionHref } from '@/lib/templateLinks';
import { BookletChip } from '../components/BookletChip';
import { TEMPLATES_COPY, TEMPLATE_VIEW_COPY } from '../copy';
import { ForceMatrix } from '../Library/ForceMatrix';
import { forceOfGroups, openCodesIn } from '../Library/paneData';
import { usePaneHeadingRef } from '../Library/paneFocus';
import { partHeadingOf, sectionCodeOf } from '../Library/templateTree';
import { useTemplate, useTemplateSet } from '../templateQueries';
import { linkedTargetOf } from './linkedTarget';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2] ONE TEMPLATE, READ-ONLY — the library's pane at `/templates/:setKey/t/:templateId`, laid
 *  out as the review site's `templateDetail` the policing team checked the booklet on: crumbs, the
 *  state and what it waits on, the force it asks for, its duty points by zone and sector as a table,
 *  and the printed totals compared.
 *
 *  ⚠️ THERE IS NO EDIT AND NO FORM. A template has one writer, the import, which creates and never
 *  updates; what it produces is an ordinary draft, reviewed before it is published.
 *
 *  ⚠️ AN IN-CHARGE IS A ROLE AND A UNIT, NEVER A PERSON — one muted line per position, and never a
 *  count of commanders (FR-5.2.6b): the force matrix counts force, and says the in-charges are apart.
 *
 *  ⚠️ [TEMPLATE-FIND] IT SHOWS THE TEMPLATE'S NUMBER AND EACH DUTY POINT'S TABLE AND ROW — the words the
 *  Decisions page names them by, so `t152 · table 1, row 34` is findable here without counting rows. Sl
 *  stays exactly as the booklet printed it; Row is PAMS's own count down the table, across pages, which
 *  is why the Page column stays beside it. A link from the Decisions page (`?point=` or `?check=`, read
 *  by `linkedTarget.ts`) marks that row or printed total, says so in words, and brings it into view;
 *  focus never moves (`paneFocus.ts`). The reverse links from an open duty point or printed total to
 *  its decision are LINKS — nothing on this pane is a control that edits.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [TEMPLATE-FIND] A MODULE-LEVEL REF, so it runs once when the linked row or check MOUNTS (or becomes
 * the linked one) and never on a re-render — a refetch must not drag the page back to it. The optional
 * CALL is required: jsdom has no `scrollIntoView` (see `HierarchyTable`).
 */
const bringIntoView = (node: HTMLElement | null): void => {
  node?.scrollIntoView?.({ block: 'center' });
};

const TextBlock = ({ title, lines, testId }: { title: string; lines: readonly string[]; testId: string }): ReactNode =>
  lines.length === 0 ? null : (
    <details className="rounded-lg border border-border bg-card px-4 py-2 text-sm" data-testid={testId}>
      <summary className="cursor-pointer font-medium text-heading">
        {title} <span className="text-xs font-normal text-muted-foreground">({lines.length})</span>
      </summary>
      <div className="mt-2 flex flex-col gap-1">
        {lines.map((line, index) => (
          <p key={`${testId}-${String(index)}`} className="whitespace-pre-wrap">
            {line}
          </p>
        ))}
      </div>
    </details>
  );

const sourceWord = (source: string | null): string => source ?? TEMPLATE_VIEW_COPY.noSource;

/** One duty point's cell in one booklet column: "4 CEN", a row per source. */
const ForceCell = ({ point, column }: { point: TemplateDutyPointDto; column: string }): ReactNode => {
  const rows = point.force.filter((row) => row.column === column && row.n > 0);
  return rows.length === 0 ? null : (
    <span className="flex flex-col">
      {rows.map((row, index) => (
        <span key={`${column}-${String(index)}`} className="whitespace-nowrap">
          {row.n} <span className="text-xs text-muted-foreground">{sourceWord(row.source)}</span>
        </span>
      ))}
    </span>
  );
};

/**
 * [TEMPLATE-FIND] "the same officer as duty point (1)" — the anchor named as the card names it, read from
 * the whole template (it may sit in another table); an anchor this template does not hold is named by
 * its key's table and row.
 */
const continuesPhrase = (anchorKey: string, pointsByKey: ReadonlyMap<string, TemplateDutyPointDto>): string =>
  TEMPLATE_VIEW_COPY.continues(templateDutyPointPhraseOf(pointsByKey.get(anchorKey) ?? { key: anchorKey, sl: null }));

/**
 * ⚠️ [TEMPLATE-FIND] A duty point as the subject of its Decide link's NAME — `duty point (4), table 1, row 7`.
 * The booklet restarts its serials, so two open rows can print one; a name of the serial alone gave both links
 * the same name. The table and row, which only one row holds, follow a serial; a row with none is already named
 * by them (`duty point at table 1, row 7`), so they are not said twice. The link's visible words do not move.
 */
const dutyPointSubjectOf = (point: TemplateDutyPointDto): string =>
  point.sl === null || point.sl === ''
    ? templateDutyPointPhraseOf(point)
    : `${templateDutyPointPhraseOf(point)}, ${templateDutyPointLabelOf({ key: point.key, sl: null })}`;

/**
 * [TEMPLATE-FIND] A table's number — the one a duty point's `table N, row M` counts — read from its first
 * key, which the import numbered; its place among the groups only where a key says none.
 */
const tableOf = (group: TemplateGroupDto, index: number): number =>
  parseTemplateDutyPointKey(group.dutyPoints[0]?.key ?? '')?.table ?? index + 1;

type GroupProps = {
  readonly group: TemplateGroupDto;
  /** Its `Table N` — `tableOf`. */
  readonly table: number;
  readonly columns: readonly string[];
  readonly setKey: string;
  readonly templateKey: string;
  readonly pointsByKey: ReadonlyMap<string, TemplateDutyPointDto>;
  /** `DUTY_POINT` codes still open on this template — `t1.g1.d2`. */
  readonly openPoints: ReadonlySet<string>;
  /** The duty point a link points at, or `null`. */
  readonly linkedKey: string | null;
};

const Group = ({
  group,
  table,
  columns,
  setKey,
  templateKey,
  pointsByKey,
  openPoints,
  linkedKey,
}: GroupProps): ReactNode => {
  if (group.dutyPoints.length === 0) return null;
  const hasReporting = group.dutyPoints.some((point) => point.reportingPlace !== null);
  const hasInCharge = group.dutyPoints.some((point) => point.inCharge.length > 0);
  const hasOther = group.dutyPoints.some((point) => point.reportTo !== null || point.remarks !== null);
  const zoneHeading = [
    group.zone && TEMPLATE_VIEW_COPY.zone(group.zone),
    group.sector && TEMPLATE_VIEW_COPY.sector(group.sector),
  ]
    .filter(Boolean)
    .join(', ');
  const heading =
    zoneHeading === '' ? TEMPLATE_VIEW_COPY.table(table) : `${TEMPLATE_VIEW_COPY.table(table)} · ${zoneHeading}`;
  const description = [group.zoneDescription, group.sectorDescription].filter(Boolean).join(' / ');
  return (
    <div className="flex flex-col gap-1.5" data-testid="template-group">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h4 className="font-medium text-heading">{heading}</h4>
        {description === '' ? null : <span className="text-sm text-muted-foreground">{description}</span>}
      </div>
      {group.supervision.length > 0 ? (
        <div className="flex flex-col text-sm text-muted-foreground">
          {group.supervision.map((line, index) => (
            <span key={`supervision-${String(index)}`}>{line}</span>
          ))}
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-2 py-2 text-left font-medium">
                {TEMPLATE_VIEW_COPY.row}
              </th>
              <th scope="col" className="px-2 py-2 text-left font-medium">
                {TEMPLATE_VIEW_COPY.sl}
              </th>
              <th scope="col" className="px-2 py-2 text-left font-medium">
                {TEMPLATE_VIEW_COPY.dutyPoint}
              </th>
              {columns.map((column) => (
                <th key={column} scope="col" className="px-2 py-2 text-left font-medium">
                  {column}
                </th>
              ))}
              {hasReporting ? (
                <th scope="col" className="px-2 py-2 text-left font-medium">
                  {TEMPLATE_VIEW_COPY.reportingPlace}
                </th>
              ) : null}
              {hasInCharge ? (
                <th scope="col" className="px-2 py-2 text-left font-medium">
                  {TEMPLATE_VIEW_COPY.inCharge}
                </th>
              ) : null}
              {hasOther ? (
                <th scope="col" className="px-2 py-2 text-left font-medium">
                  {TEMPLATE_VIEW_COPY.other}
                </th>
              ) : null}
              <th scope="col" className="px-2 py-2 text-right font-medium">
                {TEMPLATE_VIEW_COPY.pageColumn}
              </th>
            </tr>
          </thead>
          <tbody>
            {group.dutyPoints.map((point) => {
              const linked = point.key === linkedKey;
              const code = templateDutyPointCodeOf(templateKey, point.key);
              return (
                <tr
                  key={point.key}
                  ref={linked ? bringIntoView : undefined}
                  aria-current={linked ? 'true' : undefined}
                  className="border-t border-border align-top aria-[current=true]:bg-active"
                  data-testid={`template-point-${point.key}`}
                  data-round-the-clock={point.roundTheClock ? 'true' : undefined}
                >
                  <td className="px-2 py-1.5 text-xs tabular-nums text-muted-foreground">
                    {parseTemplateDutyPointKey(point.key)?.row ?? ''}
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">{point.sl ?? ''}</td>
                  <td className="px-2 py-1.5">
                    <span className="flex flex-col gap-0.5">
                      {point.place === null ? (
                        <span className="text-muted-foreground">{TEMPLATE_VIEW_COPY.noPlace}</span>
                      ) : (
                        <span className="break-words">{point.place}</span>
                      )}
                      {point.placeIssue === null ? null : (
                        <span className="text-xs text-muted-foreground">
                          {TEMPLATE_VIEW_COPY.placeIssue[point.placeIssue]}
                        </span>
                      )}
                      {point.roundTheClock ? (
                        <span className="text-xs font-medium">{TEMPLATE_VIEW_COPY.roundTheClock}</span>
                      ) : null}
                      {point.unparsedCells.map((cell, index) => (
                        <span key={`unread-${String(index)}`} className="text-xs text-muted-foreground">
                          {TEMPLATE_VIEW_COPY.unread(cell)}
                        </span>
                      ))}
                      {point.notes.map((note, index) => (
                        <span key={`note-${String(index)}`} className="text-xs text-muted-foreground">
                          {note}
                        </span>
                      ))}
                      {linked ? (
                        <span data-testid="template-linked" className="text-xs font-medium text-foreground">
                          {TEMPLATE_VIEW_COPY.linkedPoint}
                        </span>
                      ) : null}
                      {openPoints.has(code) ? (
                        <Link
                          to={templateDecisionHref(setKey, 'DUTY_POINT', code)}
                          className="self-start text-sm text-link"
                          data-testid="template-point-decide"
                          aria-label={TEMPLATE_VIEW_COPY.decideLabel(dutyPointSubjectOf(point))}
                        >
                          {TEMPLATE_VIEW_COPY.decide}
                        </Link>
                      ) : null}
                    </span>
                  </td>
                  {columns.map((column) => (
                    <td key={column} className="px-2 py-1.5 tabular-nums" data-testid="template-point-force">
                      <ForceCell point={point} column={column} />
                    </td>
                  ))}
                  {hasReporting ? <td className="px-2 py-1.5">{point.reportingPlace ?? ''}</td> : null}
                  {hasInCharge ? (
                    <td className="px-2 py-1.5 text-muted-foreground" data-testid="template-point-incharge">
                      <span className="flex flex-col gap-0.5">
                        {point.inCharge.map((position, index) => (
                          <span key={`ic-${String(index)}`}>
                            {position.role}
                            {position.unitText ? `, ${position.unitText}` : ''}
                            {position.continuesFrom ? ` (${continuesPhrase(position.continuesFrom, pointsByKey)})` : ''}
                          </span>
                        ))}
                      </span>
                    </td>
                  ) : null}
                  {hasOther ? (
                    <td className="px-2 py-1.5 text-xs">
                      <span className="flex flex-col gap-0.5">
                        {point.reportTo ? <span>{`${TEMPLATE_VIEW_COPY.reportTo}: ${point.reportTo}`}</span> : null}
                        {point.remarks ? <span>{`${TEMPLATE_VIEW_COPY.remarks}: ${point.remarks}`}</span> : null}
                      </span>
                    </td>
                  ) : null}
                  <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{point.page ?? ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

type CheckProps = {
  readonly check: TemplateCheckDto;
  readonly setKey: string;
  /** Its `TOTALS` decision is still open. */
  readonly open: boolean;
  /** A link points at it. */
  readonly linked: boolean;
};

/**
 * A printed total row against the rows read — the review site's totals check, chip and all.
 *
 * ⚠️ [TEMPLATE-FIND] THE STATUS CHIP STAYS THE FIRST CHIP — the link's words and the way to the decision
 * come after it — and the block carries its padding whether or not it is linked, so the wash appears
 * without moving anything.
 *
 * ⚠️ [TEMPLATE-FIND] ITS DECIDE LINK IS NAMED BY THE ROWS IT ADDS UP, NOT BY ITS TITLE. Two Total rows on one
 * page (`p78:t36`, `p78:t36#2`) share the title, so a name of the title alone gave both links one name;
 * `checkSubject` adds the rows, the same words the line beneath the title shows.
 */
const Check = ({ check, setKey, open, linked }: CheckProps): React.ReactElement => {
  const chip = !check.comparable ? (
    <BookletChip variant="plain">{TEMPLATE_VIEW_COPY.notCompared}</BookletChip>
  ) : check.differs ? (
    <BookletChip variant="accent">{TEMPLATE_VIEW_COPY.differs}</BookletChip>
  ) : (
    <BookletChip variant="plain">{TEMPLATE_VIEW_COPY.matches}</BookletChip>
  );
  const title = TEMPLATE_VIEW_COPY.checkTitle(check.page);
  const rows = templateDutyPointRowsOf(check.dutyPointKeys);
  return (
    <div
      ref={linked ? bringIntoView : undefined}
      aria-current={linked ? 'true' : undefined}
      className="flex flex-col gap-1 rounded-lg px-2 py-1.5 aria-[current=true]:bg-active"
      data-testid="template-check"
      data-check={check.id}
    >
      <div className="flex flex-wrap items-baseline gap-2 text-sm">
        <span className="font-medium">{title}</span>
        {chip}
        {linked ? (
          <span data-testid="template-linked" className="text-xs font-medium text-foreground">
            {TEMPLATE_VIEW_COPY.linkedCheck}
          </span>
        ) : null}
        {open ? (
          <Link
            to={templateDecisionHref(setKey, 'TOTALS', check.id)}
            className="text-sm text-link"
            data-testid="template-check-decide"
            aria-label={TEMPLATE_VIEW_COPY.decideLabel(TEMPLATE_VIEW_COPY.checkSubject(check.page, rows))}
          >
            {TEMPLATE_VIEW_COPY.decide}
          </Link>
        ) : null}
      </div>
      {check.dutyPointKeys.length > 0 ? (
        <span data-testid="template-check-rows" className="text-sm text-muted-foreground">
          {TEMPLATE_VIEW_COPY.checkRows(rows)}
        </span>
      ) : null}
      {check.columns.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm tabular-nums">
            <thead className="bg-muted text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-1.5 text-left font-medium" />
                {check.columns.map((column) => (
                  <th key={column.column} scope="col" className="px-3 py-1.5 text-right font-medium">
                    {column.column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <th scope="row" className="px-3 py-1.5 text-left font-normal">
                  {TEMPLATE_VIEW_COPY.printed}
                </th>
                {check.columns.map((column) => (
                  <td key={column.column} className="px-3 py-1.5 text-right">
                    {column.printed ?? '—'}
                  </td>
                ))}
              </tr>
              <tr className="border-t border-border">
                <th scope="row" className="px-3 py-1.5 text-left font-normal">
                  {check.dutyPointKeys.length > 0
                    ? TEMPLATE_VIEW_COPY.readFrom(check.dutyPointKeys.length)
                    : TEMPLATE_VIEW_COPY.read}
                </th>
                {check.columns.map((column) => (
                  <td
                    key={column.column}
                    className={
                      column.printed !== null && column.printed !== column.parsed
                        ? 'px-3 py-1.5 text-right font-bold'
                        : 'px-3 py-1.5 text-right'
                    }
                    data-differs={column.printed !== null && column.printed !== column.parsed ? 'true' : undefined}
                  >
                    {column.parsed}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

const TemplatePane = ({
  data,
  set,
  setKey,
}: {
  data: ArrangementTemplateDto;
  set: TemplateSetDto | undefined;
  setKey: string;
}): React.ReactElement => {
  const headingRef = usePaneHeadingRef();
  const sections = set?.sections ?? [];
  const section = sections.find((entry) => entry.key === data.sectionKey);
  const dayName = new Map((set?.days ?? []).map((day) => [day.key, day.name] as const));
  const word = templateStateWord(data);
  const dutyPoints = data.groups.flatMap((group) => group.dutyPoints);
  const columns = [...new Set(dutyPoints.flatMap((point) => point.force.map((row) => row.column)))];
  const roundTheClockWhole = data.readiness.state === 'ROUND_THE_CLOCK';
  const someRoundTheClock = dutyPoints.some((point) => point.roundTheClock);
  const totals = roundTheClockWhole ? forceOfGroups(data.groups, TEMPLATE_SOURCE_NONE) : data.force;
  /* [TEMPLATE-FIND] What a link points at — from the URL as it is NOW, never a copy taken on mount. */
  const [searchParams] = useSearchParams();
  const target = linkedTargetOf(searchParams, data);
  const pointsByKey = new Map(dutyPoints.map((point) => [point.key, point] as const));
  const openPoints = openCodesIn([data], 'DUTY_POINT');
  const openTotals = openCodesIn([data], 'TOTALS');

  return (
    <article className="flex flex-col gap-5" data-testid="template-view">
      <header className="flex flex-col gap-1">
        <nav aria-label={TEMPLATE_VIEW_COPY.crumbs} className="flex flex-wrap gap-x-2 text-sm">
          <Link to={`/templates/${setKey}/part/${encodeURIComponent(data.part)}`} className="text-link">
            {partHeadingOf(data.part, sections)}
          </Link>
          <span className="text-muted-foreground" aria-hidden>
            /
          </span>
          <Link to={`/templates/${setKey}/section/${encodeURIComponent(data.sectionKey)}`} className="text-link">
            {`${sectionCodeOf(data.sectionKey)}: ${section?.title ?? data.sectionTitle}`}
          </Link>
        </nav>
        <h2 key={data.id} ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-heading outline-none">
          {data.title}
        </h2>
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground" data-testid="template-meta">
          <span data-testid="template-number">{TEMPLATE_VIEW_COPY.number(data.key)}</span>
          {data.divisionName ? <span>{data.divisionName}</span> : null}
          {data.pages.length > 0 ? <span>{TEMPLATE_VIEW_COPY.pages(data.pages)}</span> : null}
          <span>{TEMPLATE_VIEW_COPY.dutyPoints(data.dutyPointCount)}</span>
          {data.dayKeys.length > 0 ? (
            <span>
              {data.dayKeys.map((key) => dayName.get(key) ?? key).join(', ')}
              {data.scheduleDecided ? '' : ` (${TEMPLATE_VIEW_COPY.proposed})`}
            </span>
          ) : null}
        </p>
      </header>

      <section className="flex flex-col gap-2 text-sm" data-testid="template-state">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={word.tone} data-testid="template-state-pill">
            {word.label}
          </StatusPill>
          <span className="text-xs text-muted-foreground">{TEMPLATE_VIEW_COPY.readOnly}</span>
        </div>
        {data.readiness.reasons.length > 0 ? (
          <ul className="flex list-disc flex-col gap-0.5 pl-5" data-testid="template-reasons">
            {data.readiness.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : null}
        {data.reviewNotes.length > 0 ? (
          <div
            className="flex flex-col gap-0.5 rounded-lg border border-border bg-muted px-3 py-2"
            data-testid="template-review-notes"
          >
            <span className="font-medium">{TEMPLATE_VIEW_COPY.reviewNotes}</span>
            {data.reviewNotes.map((note, index) => (
              <span key={`review-${String(index)}`}>{note}</span>
            ))}
          </div>
        ) : null}
      </section>

      <section className="flex flex-col gap-1 text-sm" data-testid="template-produced">
        <h3 className="font-medium text-heading">{TEMPLATE_VIEW_COPY.produced}</h3>
        {data.produced.length === 0 ? (
          <span className="text-muted-foreground">{TEMPLATE_VIEW_COPY.nothingProduced}</span>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {data.produced.map((order) => (
              <li key={order.arrangementId} className="flex items-center gap-2">
                <Link to={`/arrangements/${order.arrangementId}`} className="text-link">
                  {order.paNumber ?? order.reference}
                </Link>
                <span className="text-muted-foreground">{orderStatusWord(order).label}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.supervision.length > 0 ? (
        <section className="flex flex-col gap-1 text-sm" data-testid="template-supervision">
          <h3 className="font-medium text-heading">{TEMPLATE_VIEW_COPY.supervision}</h3>
          {data.supervision.map((line, index) => (
            <p key={`supervision-${String(index)}`} className="whitespace-pre-wrap">
              {line}
            </p>
          ))}
        </section>
      ) : null}
      <TextBlock title={TEMPLATE_VIEW_COPY.info} lines={data.info} testId="template-info" />
      <TextBlock title={TEMPLATE_VIEW_COPY.instructions} lines={data.instructions} testId="template-instructions" />
      <TextBlock title={TEMPLATE_VIEW_COPY.notes} lines={data.notes} testId="template-notes" />

      <section className="flex flex-col gap-2" data-testid="template-force">
        <h3 className="font-medium text-heading">{TEMPLATE_VIEW_COPY.forceRequired}</h3>
        <ForceMatrix
          totals={totals}
          openColumns={openCodesIn([data], 'FORCE_COLUMN')}
          openSources={openCodesIn([data], 'SOURCE')}
          caption={
            roundTheClockWhole
              ? TEMPLATE_VIEW_COPY.forceRoundTheClock
              : someRoundTheClock
                ? `${TEMPLATE_VIEW_COPY.forceCaption} ${TEMPLATE_VIEW_COPY.forceSomeRoundTheClock}`
                : TEMPLATE_VIEW_COPY.forceCaption
          }
          testId="template-force-matrix"
        />
      </section>

      <section className="flex flex-col gap-3" data-testid="template-groups">
        <h3 className="font-medium text-heading">{TEMPLATE_VIEW_COPY.groups}</h3>
        {data.groups.map((group, index) => (
          <Group
            key={`group-${String(index)}`}
            group={group}
            table={tableOf(group, index)}
            columns={columns}
            setKey={setKey}
            templateKey={data.key}
            pointsByKey={pointsByKey}
            openPoints={openPoints}
            linkedKey={target?.kind === 'point' ? target.key : null}
          />
        ))}
      </section>

      {data.checks.length > 0 ? (
        <section className="flex flex-col gap-3" data-testid="template-checks">
          <h3 className="font-medium text-heading">{TEMPLATE_VIEW_COPY.checks}</h3>
          <p data-testid="template-checks-caption" className="text-sm text-muted-foreground">
            {TEMPLATE_VIEW_COPY.checksCaption}
          </p>
          {data.checks.map((check) => (
            <Check
              key={check.id}
              check={check}
              setKey={setKey}
              open={openTotals.has(check.id)}
              linked={target?.kind === 'check' && target.id === check.id}
            />
          ))}
        </section>
      ) : null}
    </article>
  );
};

/**
 * ⚠️ THE PANE WAITS FOR THE SET AS WELL AS THE TEMPLATE. The set names the festival days and the Part's
 * heading; rendered first, the pane flashed the day KEYS (`d2, d3`) and a bare "Part I" — a key on a
 * screen, which this page promises never to show. Both reads share the library's cache, so inside the
 * library this costs nothing.
 * ⚠️ [amended TEMPLATE-FIND] Day keys are still never shown. The template's number (`Template t152`) and
 * each duty point's table and row ARE shown, on purpose: they are the words the Decisions page names a
 * template and a duty point by, so this narrows #1127's promise to day keys and reverses it for numbers.
 *
 * ⚠️ [TEMPLATE-FIND] THE PANE IS KEYED ON THE TEMPLATE, AND A LINK IS NOT WHY. Every link the product makes
 * to a duty point or a printed total (`templateHref`) goes through the library's `?t=` entry, whose
 * `<Navigate replace>` unmounts this view first — a link always arrives at a new pane. What does NOT is
 * HISTORY: Back and Forward move one `TemplateView` straight between two `/t/<id>?point=` entries, and with
 * both templates cached there is no spinner between them, so the pane is reused. Row keys repeat across
 * templates (`g1.d1` is in nearly every one), so without the key the same `<tr>` re-renders linked in both —
 * its ref never runs again and the other template's row is never brought into view. The key makes a new
 * pane for each template, however it was reached.
 */
const TemplateView = (): React.ReactElement => {
  const { setKey = '', templateId = '' } = useParams();
  const template = useTemplate(setKey, templateId);
  const set = useTemplateSet(setKey, { pane: true });
  if (template.isPending || set.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }
  return template.data ? (
    <TemplatePane key={template.data.id} data={template.data} set={set.data} setKey={setKey} />
  ) : (
    <EmptyState title={TEMPLATES_COPY.noTemplate} />
  );
};

export default TemplateView;
